import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bell, BellOff, Clock, Loader2, Moon } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';

/**
 * A person's own Slack notification settings (Task 5).
 *
 * WHY THIS LIVES IN SETTINGS AND NOT ON THE INTEGRATIONS PAGE.
 * Admin → Identity & Setup → Integrations is admin-only — it is where a workspace is connected and
 * where other people are mapped. These settings belong to whoever is looking at
 * them, and most of the people who will use them are not admins. That is the
 * same reason the server mounts these two endpoints on a router that skips
 * `requireAdmin` while keeping authentication and the tenant gate. Putting the
 * screen behind an admin route would have made the non-admin endpoints
 * unreachable in practice, which is a permission bug that looks like a missing
 * feature.
 *
 * It is still wrapped in ExternalIntegrationsGate by its caller, so a tenant
 * that cannot use integrations never sees the tab at all.
 *
 * THREE HONEST STATES, AND THE MIDDLE ONE IS TODAY'S.
 *   not connected — nobody can be mapped until the Slack app exists, so this
 *                   is what everyone sees right now. It says so plainly rather
 *                   than showing dead controls.
 *   connected     — the real controls.
 *   error         — says the settings could not be loaded, and does not
 *                   pretend the defaults below are the person's own.
 */

/** The triggers that can actually fire today, in the order they matter. */
const TRIGGERS: Array<{ value: string; label: string; help: string }> = [
  {
    value: 'task_assigned',
    label: 'A task is assigned to me',
    help: 'Sent when someone makes you the owner of a task.',
  },
  {
    value: 'at_risk',
    label: 'A task is flagged At Risk',
    help: 'Sent when the status changes to At Risk.',
  },
  {
    value: 'collaborator_added',
    label: "I'm added as a collaborator",
    help: 'Sent when someone adds you to a task you do not own.',
  },
  {
    value: 'mention',
    label: "I'm @mentioned on a task",
    help: 'Sent when someone tags you in a task comment.',
  },
  {
    value: 'comment_added',
    label: 'Someone comments on a task I am on',
    help: 'Sent when you are the assignee or a collaborator and someone else comments.',
  },
  {
    value: 'due_soon',
    label: 'A task of mine is due soon',
    help: 'How far ahead is set below.',
  },
  {
    value: 'overdue',
    label: 'A task of mine is overdue',
    help: 'Sent once, a day after the due date passes.',
  },
  {
    value: 'weekly_digest',
    label: 'Monday digest of my week',
    help: 'One message on Monday morning with everything open.',
  },
];

/**
 * A short list of zones plus whatever this browser is in.
 *
 * Not the full IANA database: a 400-entry dropdown to pick "when should Jarvis
 * leave me alone" is worse than six sensible options and the one the person is
 * actually sitting in. The server accepts any zone Node can resolve, so this
 * list is a convenience, not the contract.
 */
const COMMON_ZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Asia/Kolkata',
];

const LEAD_MIN = 4;
const LEAD_MAX = 168;

interface QuietHours {
  start: string;
  end: string;
  timezone: string;
}

interface Mapping {
  slack_display_name: string | null;
  slack_email: string | null;
  notifications_enabled: boolean;
  muted_types: string[];
  due_soon_lead_hours: number;
  quiet_hours: QuietHours | null;
}

/** "48 hours" reads worse than "2 days" past a certain point, and vice versa. */
function describeLead(hours: number): string {
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} before it is due`;
  const days = hours / 24;
  const rounded = Number.isInteger(days) ? days : Math.round(days * 10) / 10;
  return `${rounded} day${rounded === 1 ? '' : 's'} before it is due`;
}

function browserZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
  } catch {
    return 'America/New_York';
  }
}

const Toggle: React.FC<{
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label: string;
}> = ({ checked, onChange, disabled, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
      checked ? 'bg-blue-600' : 'bg-gray-200'
    } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${
        checked ? 'translate-x-5' : 'translate-x-0.5'
      }`}
    />
  </button>
);

const SlackNotificationSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mapping, setMapping] = useState<Mapping | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const { data } = await api.get('/api/integrations/slack/me');
      setMapping(data.mapping || null);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Every change is a round trip, and the answer replaces local state.
   *
   * No optimistic update: this screen decides whether a person's work reaches
   * their phone at 2am, and a toggle that looks saved but is not is the exact
   * failure the epic's opt-out promise cannot survive. The request is small and
   * the server returns the whole mapping, so the screen always shows what is
   * actually stored.
   */
  const save = useCallback(
    async (patch: Record<string, unknown>, successMessage: string) => {
      setSaving(true);
      try {
        const { data } = await api.patch('/api/integrations/slack/me', patch);
        setMapping(data.mapping);
        toast.success(successMessage);
      } catch (err: any) {
        toast.error(err?.response?.data?.error || 'Could not save that. Nothing was changed.');
      } finally {
        setSaving(false);
      }
    },
    []
  );

  const muted = useMemo(() => new Set(mapping?.muted_types || []), [mapping]);

  const [leadDraft, setLeadDraft] = useState<number>(48);
  useEffect(() => {
    if (mapping) setLeadDraft(mapping.due_soon_lead_hours);
  }, [mapping]);

  const [quietDraft, setQuietDraft] = useState<QuietHours>({
    start: '22:00',
    end: '07:00',
    timezone: browserZone(),
  });
  useEffect(() => {
    if (mapping?.quiet_hours) setQuietDraft(mapping.quiet_hours);
  }, [mapping]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-8">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading your notification settings…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        <p className="font-medium">Could not load your notification settings.</p>
        <p className="mt-1">
          Nothing has been changed.{' '}
          <button type="button" onClick={load} className="underline font-medium">
            Try again
          </button>
          .
        </p>
      </div>
    );
  }

  // Nobody can be mapped until the Slack app exists, so this is what everyone
  // sees today. Say that, rather than showing controls that save nothing.
  if (!mapping) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-4 text-sm text-gray-700">
        <div className="flex items-center gap-2 font-medium text-gray-900">
          <BellOff className="w-4 h-4 text-gray-400" />
          You are not connected to Slack
        </div>
        <p className="mt-2 text-gray-600">
          Jarvis is not sending you anything in Slack, so there is nothing to configure yet. Once
          an administrator connects the workspace and links your account, your notification
          settings appear here.
        </p>
      </div>
    );
  }

  const master = mapping.notifications_enabled;

  return (
    <div className="space-y-8">
      {/* ── Master switch ─────────────────────────────────────────────── */}
      <section>
        <div className="flex items-start justify-between gap-6">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Bell className="w-4 h-4 text-gray-400" />
              Slack notifications
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              {mapping.slack_display_name || mapping.slack_email
                ? `Linked to ${mapping.slack_display_name || mapping.slack_email} in Slack.`
                : 'Your Jarvis account is linked to Slack.'}{' '}
              Turning this off stops everything below, including the Monday digest.
            </p>
          </div>
          <Toggle
            checked={master}
            disabled={saving}
            label="All Slack notifications"
            onChange={(next) =>
              save({ opt_out: !next }, next ? 'Slack notifications on.' : 'Slack notifications off.')
            }
          />
        </div>
      </section>

      {/* Everything below is subordinate to the master switch, and says so by
          going quiet rather than disappearing — a person turning notifications
          back on should find their old choices where they left them. */}
      <div className={master ? '' : 'opacity-50 pointer-events-none select-none'}>
        {/* ── Per-type ──────────────────────────────────────────────────── */}
        <section>
          <h3 className="text-sm font-semibold text-gray-900">What to tell me about</h3>
          <div className="mt-3 divide-y divide-gray-100 rounded-lg border border-gray-200">
            {TRIGGERS.map((t) => {
              const on = !muted.has(t.value);
              return (
                <div key={t.value} className="flex items-start justify-between gap-6 px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{t.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{t.help}</p>
                  </div>
                  <Toggle
                    checked={on}
                    disabled={saving}
                    label={t.label}
                    onChange={(next) => {
                      const nextMuted = new Set(muted);
                      if (next) nextMuted.delete(t.value);
                      else nextMuted.add(t.value);
                      save({ muted_types: [...nextMuted] }, 'Saved.');
                    }}
                  />
                </div>
              );
            })}
          </div>
        </section>

        {/* ── Lead time ─────────────────────────────────────────────────── */}
        <section className="mt-8">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-400" />
            How much warning before a due date
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            Tell me <span className="font-medium text-gray-900">{describeLead(leadDraft)}</span>.
          </p>
          <div className="mt-3 flex items-center gap-4">
            <input
              type="range"
              min={LEAD_MIN}
              max={LEAD_MAX}
              step={4}
              value={leadDraft}
              disabled={saving}
              onChange={(e) => setLeadDraft(Number(e.target.value))}
              // Saved on release, not on every pixel — a range input fires an
              // event per step and a save per step is a hundred writes a drag.
              onMouseUp={() => save({ due_soon_lead_hours: leadDraft }, 'Lead time saved.')}
              onTouchEnd={() => save({ due_soon_lead_hours: leadDraft }, 'Lead time saved.')}
              onKeyUp={() => save({ due_soon_lead_hours: leadDraft }, 'Lead time saved.')}
              className="flex-1 accent-blue-600"
              aria-label="Hours of warning before a task is due"
            />
            <span className="text-sm tabular-nums text-gray-500 w-20 text-right">
              {leadDraft} hours
            </span>
          </div>
        </section>

        {/* ── Quiet hours ───────────────────────────────────────────────── */}
        <section className="mt-8">
          <div className="flex items-start justify-between gap-6">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <Moon className="w-4 h-4 text-gray-400" />
                Quiet hours
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                Nothing is dropped during quiet hours — messages are held and delivered when the
                window ends.
              </p>
            </div>
            <Toggle
              checked={Boolean(mapping.quiet_hours)}
              disabled={saving}
              label="Quiet hours"
              onChange={(next) =>
                save(
                  { quiet_hours: next ? quietDraft : null },
                  next ? 'Quiet hours on.' : 'Quiet hours off.'
                )
              }
            />
          </div>

          {mapping.quiet_hours && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <label className="block">
                <span className="text-xs font-medium text-gray-700">From</span>
                <input
                  type="time"
                  value={quietDraft.start}
                  disabled={saving}
                  onChange={(e) => setQuietDraft({ ...quietDraft, start: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-700">Until</span>
                <input
                  type="time"
                  value={quietDraft.end}
                  disabled={saving}
                  onChange={(e) => setQuietDraft({ ...quietDraft, end: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-700">Timezone</span>
                <select
                  value={quietDraft.timezone}
                  disabled={saving}
                  onChange={(e) => setQuietDraft({ ...quietDraft, timezone: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
                >
                  {/* The browser's own zone first, and never duplicated. */}
                  {[browserZone(), ...COMMON_ZONES.filter((z) => z !== browserZone())].map((z) => (
                    <option key={z} value={z}>
                      {z.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}

          {mapping.quiet_hours && (
            <div className="mt-3 flex items-center gap-3">
              <button
                type="button"
                disabled={saving}
                onClick={() => save({ quiet_hours: quietDraft }, 'Quiet hours saved.')}
                className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save quiet hours'}
              </button>
              <span className="text-xs text-gray-500">
                A window may cross midnight — 22:00 until 07:00 is fine.
              </span>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default SlackNotificationSettings;
